import { ContactSupportMessageType } from "@redotech/redo-model/conversation/message-parsers/contact-support-message-parser";
import { memo } from "react";
import { HandleEmailInputFn, HandleLiveChatHandoffFn } from "../common";
import { AiEmailInput } from "./ai-email-input";
import { ChatContentRenderer } from "./chat-content-renderer";
import {
  GenericConfirmedState,
  GenericRejectedState,
} from "./chat-content-states";
import { getConfirmedStateContent } from "./tool-message-utils";

export type ContactSupportMode = "email" | "live_chat";
export const ContactSupport = memo(function ContactSupport({
  message,
  readOnly,
  onEmailEntry,
  onLiveChatHandoff,
  conversationId,
  buttonText,
  messageSide = "left",
  mode = "email",
}: {
  message: ContactSupportMessageType;
  readOnly: boolean;
  onEmailEntry?: HandleEmailInputFn | undefined;
  onLiveChatHandoff?: HandleLiveChatHandoffFn | undefined;
  conversationId: string;
  buttonText: string;
  messageSide?: "left" | "right";
  mode?: ContactSupportMode;
}) {
  const isLiveChatMode = mode === "live_chat";

  const { title: confirmedTitle, subtitle: confirmedSubtitle } =
    getConfirmedStateContent(
      message,
      isLiveChatMode
        ? "You'll be connected to a live agent shortly!"
        : `We'll get back to you ${message.structuredContent?.customerEmail ? `at ${message.structuredContent?.customerEmail}` : "over email."}`,
      "Something went wrong contacting support.",
      isLiveChatMode
        ? "Connecting to live support..."
        : "Thank you for contacting support.",
    );

  return (
    <ChatContentRenderer
      contentByState={{
        pending: (
          <AiEmailInput
            buttonText={buttonText}
            conversationId={conversationId}
            message={message}
            messageSide={messageSide}
            mode={mode}
            onEmailEntry={isLiveChatMode ? onLiveChatHandoff : onEmailEntry}
            readOnly={readOnly}
          />
        ),
        confirmed: (
          <GenericConfirmedState
            subtitle={confirmedSubtitle}
            title={confirmedTitle}
          />
        ),
        rejected: (
          <GenericRejectedState
            subtitle="Please try again or contact us directly"
            title="Unable to submit support request"
          />
        ),
      }}
      message={message}
    />
  );
});
